/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AmountJson,
  AmountString,
  Amounts,
  FRAC_SEPARATOR,
  HostPortPath,
  HttpStatusCode,
  IbanString,
  PaytoType,
  Paytos,
  TalerErrorCode,
  TranslatedString,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  AsyncButton,
  InternationalizationAPI,
  RenderAmount,
  RouteDefinition,
  ShowInputErrorLabel,
  useBankCoreApiContext,
  useNotificationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { ComponentChildren, Fragment, Ref, VNode, h } from "preact";
import { useState } from "preact/hooks";
import { useBankChallengeHandlerContext } from "../context/challenge.js";
import { OperationError } from "../components/OperationError.js";
import { LoggedIn, useSessionState } from "../hooks/session.js";
import { undefinedIfEmpty, validateIBAN, validateTalerBank } from "../utils.js";
import { IntAmountJson, IntAmounts } from "./regional/CreateCashout.js";

export interface Props {
  focus?: boolean;
  withAccount?: string;
  withSubject?: string;
  withAmount?: string;
  onSuccess?: () => void;
  routeCancel?: RouteDefinition;
  routeCashout?: RouteDefinition;
  limit: IntAmountJson;
  debtLimit: AmountJson;
}

export function PaytoWireTransferForm({
  focus,
  withAccount,
  withSubject,
  withAmount,
  onSuccess,
  routeCancel,
  routeCashout,
  limit,
  debtLimit,
}: Props): VNode {
  const [inputType, setInputType] = useState<"form" | "payto">("form");
  const isRawPayto = inputType === "payto";

  const { state: credentials } = useSessionState();
  const creds = credentials.status === "loggedIn" ? credentials : undefined;
  const {
    lib: { bank: api },
    config,
    url,
  } = useBankCoreApiContext();

  const sendingToFixedAccount = withAccount !== undefined;

  const [account, setAccount] = useState<string | undefined>(withAccount);
  const [subject, setSubject] = useState<string | undefined>(withSubject);
  const [amount, setAmount] = useState<string | undefined>(withAmount);

  const [rawPaytoInput, rawPaytoInputSetter] = useState<string | undefined>(
    undefined,
  );
  const { i18n } = useTranslationContext();
  const wireFee =
    config.wire_transfer_fees === undefined
      ? Amounts.zeroOfCurrency(config.currency)
      : Amounts.parseOrThrow(config.wire_transfer_fees);

  const trimmedAmountStr = amount?.trim();
  const limitWithFee = IntAmounts.from(limit)
    .deduce(wireFee)
    .getResultZeroIfNegative();
  const parsedAmount = Amounts.parse(
    `${limitWithFee.currency}:${trimmedAmountStr}`,
  );
  const { showError } = useNotificationContext();
  const mfa = useBankChallengeHandlerContext();

  const paytoType =
    config.wire_type === "X_TALER_BANK"
      ? ("x-taler-bank" as const)
      : ("iban" as const);

  const errorsWire = undefinedIfEmpty({
    account: !account
      ? i18n.str`Required`
      : paytoType === "iban"
        ? validateIBAN(account, i18n)
        : paytoType === "x-taler-bank"
          ? validateTalerBank(account, i18n)
          : undefined,
    subject: !subject ? i18n.str`Required` : validateSubject(subject, i18n),
    amount: !trimmedAmountStr
      ? i18n.str`Required`
      : !parsedAmount
        ? i18n.str`Not valid`
        : validateAmount(parsedAmount, limitWithFee, i18n),
  });

  const foundPayto = searchPaytoInHumanReadableText(rawPaytoInput);
  const parsed = !foundPayto ? undefined : Paytos.fromString(foundPayto);

  const errorsPayto = undefinedIfEmpty({
    rawPaytoInput: !rawPaytoInput
      ? i18n.str`Required`
      : !parsed || parsed.tag === "error"
        ? i18n.str`Does not follow the pattern`
        : validateRawPayto(
            parsed.value,
            limitWithFee,
            url.host,
            i18n,
            paytoType,
          ),
  });

  let parsedURI: Paytos.URI | undefined;
  let sendingAmount: AmountString | undefined;

  if (isRawPayto) {
    if (parsed?.tag === "ok") {
      parsedURI = {
        ...parsed.value,
        params: { ...parsed.value.params },
      };
      sendingAmount = parsedURI.params.amount as AmountString;
      delete parsedURI.params.amount; // we don't want to send twice in the request
    }
  } else if (account && subject) {
    switch (paytoType) {
      case "x-taler-bank": {
        parsedURI = Paytos.createTalerBank(url.href as HostPortPath, account);
        break;
      }
      case "iban": {
        parsedURI = Paytos.createIban(account as IbanString, undefined);
        break;
      }
      default:
        assertUnreachable(paytoType);
    }

    parsedURI.params.message = subject;
    sendingAmount =
      `${limitWithFee.currency}:${trimmedAmountStr}` as AmountString;
  }
  const sAmount = sendingAmount;
  const sendDisabled =
    (isRawPayto ? !!errorsPayto : !!errorsWire) ||
    !sAmount ||
    !parsedURI ||
    !creds;

  // i18n.str`send transaction`,
  const send = useNotifiedOperation<
    Awaited<ReturnType<typeof api.createTransaction>>,
    [LoggedIn, AmountString, Paytos.URI, challengeIds?: string[]]
  >(
    (
      ct,
      creds: LoggedIn,
      amount: AmountString,
      uri: Paytos.URI,
      challengeIds?: string[],
    ) =>
      api.createTransaction(
        creds,
        { payto_uri: Paytos.toFullString(uri), amount },
        { challengeIds },
      ),
    {
      onSuccess: () => {
        mfa.cancel();
        // notifyInfo(i18n.str`The wire transfer was successfully completed!`);
        if (onSuccess) onSuccess();
        setAmount(undefined);
        setAccount(undefined);
        setSubject(undefined);
        rawPaytoInputSetter(undefined);
      },
      onFail: showError(
        i18n.str`Failed to create the transaction.`,
        (fail, creds, amount, uri) => {
          switch (fail.case) {
            case HttpStatusCode.BadRequest:
              return i18n.str`The request was invalid or the payto://-URI used unacceptable features.`;
            case HttpStatusCode.Unauthorized:
              return i18n.str`Not enough permission to complete the operation.`;
            case TalerErrorCode.BANK_ADMIN_CREDITOR:
              return i18n.str`The bank administrator cannot be the transfer creditor.`;
            case TalerErrorCode.BANK_UNKNOWN_CREDITOR:
              return i18n.str`The destination account "${uri.displayName}" was not found.`;
            case TalerErrorCode.BANK_SAME_ACCOUNT:
              return i18n.str`The origin and the destination of the transfer can't be the same.`;
            case TalerErrorCode.BANK_UNALLOWED_DEBIT:
              return i18n.str`Your balance is not sufficient for the operation.`;
            case HttpStatusCode.NotFound:
              return i18n.str`The origin account "${uri.displayName}" was not found.`;
            case TalerErrorCode.BANK_TRANSFER_REQUEST_UID_REUSED:
              return i18n.str`The attempt to create the transaction has failed. Please try again.`;
            case HttpStatusCode.Accepted:
              mfa.onNewChallenge(
                i18n.str`Confirm transaction.`,
                creds.username,
                fail.body,
                {
                  running: send.running,
                  cancel: send.cancel,
                  run: (challengeIds) =>
                    send.run(creds, amount, uri, challengeIds),
                },
              );
              return undefined;
            default:
              assertUnreachable(fail);
          }
        },
      ),
    },
  );

  function switchToForm(): void {
    if (parsed?.tag === "ok") {
      switch (parsed.value.targetType) {
        case PaytoType.Ethereum:
        case PaytoType.Bitcoin:
        case undefined:
        case PaytoType.TalerReserve:
        case PaytoType.Void:
        case PaytoType.TalerReserveHttp:
          break;
        case PaytoType.IBAN:
          setAccount(parsed.value.iban);
          break;
        case PaytoType.TalerBank:
        case PaytoType.Cyclos:
          setAccount(parsed.value.account);
          break;
        default:
          assertUnreachable(parsed.value);
      }
      const paytoAmount = parsed.value.params.amount;
      if (paytoAmount) {
        const parsedPaytoAmount = Amounts.parse(paytoAmount);
        if (parsedPaytoAmount) {
          setAmount(Amounts.stringifyValue(parsedPaytoAmount));
        }
      }
      const paytoSubject =
        parsed.value.params.message ?? parsed.value.params.subject;
      if (paytoSubject) setSubject(paytoSubject);
    }
    setInputType("form");
  }

  function switchToPayto(): void {
    if (account) {
      let payto: Paytos.URI;
      switch (paytoType) {
        case "x-taler-bank":
          payto = Paytos.createTalerBank(url.href as HostPortPath, account);
          break;
        case "iban":
          payto = Paytos.createIban(account as IbanString, undefined);
          break;
        default:
          assertUnreachable(paytoType);
      }
      if (parsedAmount) {
        payto.params.amount = Amounts.stringify(parsedAmount);
      }
      if (subject) payto.params.message = subject;
      rawPaytoInputSetter(Paytos.toFullString(payto));
    }
    setInputType("payto");
  }

  const parsedSendingAmount = sAmount ? Amounts.parse(sAmount) : undefined;
  const totalResult =
    parsedSendingAmount?.currency === wireFee.currency
      ? Amounts.add(parsedSendingAmount, wireFee)
      : undefined;
  const totalDebit = totalResult?.saturated ? undefined : totalResult?.amount;

  return (
    <form
      class="mt-6 overflow-hidden rounded-xl border border-onBackground/10 bg-white shadow-sm"
      autoCapitalize="none"
      autoCorrect="off"
      onSubmit={(e) => e.preventDefault()}
    >
      <div class="border-b border-onBackground/10 bg-primary/5 px-5 py-5 sm:px-8">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 class="text-lg font-semibold text-onBackground">
              <i18n.Translate>Transfer details</i18n.Translate>
            </h2>
            <p class="mt-1 text-sm text-gray-600">
              <i18n.Translate>
                Enter the recipient, a reference, and the amount to send.
              </i18n.Translate>
            </p>
          </div>
          <div class="shrink-0 rounded-lg bg-white px-4 py-3 ring-1 ring-primary/15">
            <div class="text-xs font-medium uppercase tracking-wide text-gray-500">
              <i18n.Translate>Maximum transfer</i18n.Translate>
            </div>
            <div class="mt-1 text-lg font-semibold text-secondary">
              <RenderAmount
                value={limitWithFee}
                spec={config.currency_specification}
              />
            </div>
            <div class="mt-3 flex items-baseline justify-between gap-4 border-t border-gray-200 pt-3 text-xs text-gray-500">
              <span class="font-medium">
                <i18n.Translate>Debt limit</i18n.Translate>
              </span>
              <span class="font-semibold text-secondary">
                <RenderAmount
                  value={debtLimit}
                  spec={config.currency_specification}
                />
              </span>
            </div>
            <p class="mt-1 max-w-xs text-xs text-gray-500">
              {Amounts.isZero(debtLimit) ? (
                <i18n.Translate>
                  This is not your account balance. This account cannot go into
                  debt.
                </i18n.Translate>
              ) : (
                <i18n.Translate>
                  This is not your account balance. It includes borrowing up to
                  the debt limit shown here.
                </i18n.Translate>
              )}
            </p>
          </div>
        </div>

        {!sendingToFixedAccount ? (
          <fieldset class="mt-5">
            <legend class="sr-only">
              <i18n.Translate>Transfer input method</i18n.Translate>
            </legend>
            <div class="inline-flex rounded-lg bg-onBackground/5 p-1">
              <label
                data-checked={inputType === "form"}
                class="cursor-pointer rounded-md px-3 py-2 text-sm font-medium text-gray-600 data-[checked=true]:bg-white data-[checked=true]:text-brand data-[checked=true]:shadow-sm"
              >
                <input
                  type="radio"
                  name="input-type"
                  onChange={switchToForm}
                  checked={inputType === "form"}
                  value="form"
                  class="sr-only"
                />
                <i18n.Translate>Enter details</i18n.Translate>
              </label>
              <label
                data-checked={inputType === "payto"}
                class="cursor-pointer rounded-md px-3 py-2 text-sm font-medium text-gray-600 data-[checked=true]:bg-white data-[checked=true]:text-brand data-[checked=true]:shadow-sm"
              >
                <input
                  type="radio"
                  name="input-type"
                  onChange={switchToPayto}
                  checked={inputType === "payto"}
                  value="payto"
                  class="sr-only"
                />
                <i18n.Translate>Paste payto URI</i18n.Translate>
              </label>
            </div>
          </fieldset>
        ) : undefined}
      </div>

      <div class="px-5 py-6 sm:px-8">
        <div class="max-w-2xl">
          {!isRawPayto ? (
            <div class="grid grid-cols-1 gap-y-6">
              {(() => {
                switch (paytoType) {
                  case "x-taler-bank":
                    return (
                      <TextField
                        id="x-taler-bank"
                        required
                        label={i18n.str`Recipient`}
                        help={i18n.str`Account name of the recipient`}
                        error={errorsWire?.account}
                        onChange={setAccount}
                        value={account}
                        placeholder={i18n.str`username`}
                        focus={focus}
                        disabled={sendingToFixedAccount}
                      />
                    );
                  case "iban":
                    return (
                      <TextField
                        id="iban"
                        required
                        label={i18n.str`Recipient`}
                        help={i18n.str`IBAN of the recipient's account`}
                        placeholder={"CC0123456789" as TranslatedString}
                        error={errorsWire?.account}
                        onChange={(value) => setAccount(value.toUpperCase())}
                        value={account}
                        focus={focus}
                        disabled={sendingToFixedAccount}
                      />
                    );
                  default:
                    assertUnreachable(paytoType);
                }
              })()}

              <div class="sm:col-span-5">
                <label
                  for="subject"
                  class="block text-sm font-medium leading-6 text-onBackground"
                >
                  {i18n.str`Transfer reference`}
                  <b class="text-[red]"> *</b>
                </label>
                <div class="mt-2">
                  <textarea
                    rows={2}
                    class="block w-full rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    name="subject"
                    id="subject"
                    autocomplete="off"
                    placeholder={i18n.str`What is this transfer for?`}
                    value={subject ?? ""}
                    required
                    onInput={(e) => setSubject(e.currentTarget.value)}
                  />
                  <ShowInputErrorLabel
                    message={errorsWire?.subject}
                    isDirty={subject !== undefined}
                  />
                </div>
              </div>

              <div class="sm:col-span-5">
                <label
                  for="amount"
                  class="block text-sm font-medium leading-6 text-onBackground"
                >
                  {i18n.str`Amount`}
                  <b class="text-[red]"> *</b>
                </label>
                <InputAmount
                  name="amount"
                  left
                  currency={limitWithFee.currency}
                  value={trimmedAmountStr}
                  onChange={setAmount}
                />
                <ShowInputErrorLabel
                  message={errorsWire?.amount}
                  isDirty={trimmedAmountStr !== undefined}
                />
              </div>
            </div>
          ) : (
            <div>
              <label
                for="address"
                class="block text-sm font-medium leading-6 text-onBackground"
              >
                {i18n.str`Payto URI`}
                <b class="text-[red]"> *</b>
              </label>
              <p class="mt-1 text-sm text-gray-500">
                <i18n.Translate>
                  Paste a payto URI containing the recipient, amount, and
                  transfer reference.
                </i18n.Translate>
              </p>
              <div class="mt-2">
                <textarea
                  ref={focus ? doAutoFocus : undefined}
                  name="address"
                  id="address"
                  rows={5}
                  class="block w-full rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                  value={rawPaytoInput ?? ""}
                  required
                  title={i18n.str`Uniform resource identifier of the target account`}
                  placeholder={((): TranslatedString => {
                    switch (paytoType) {
                      case "x-taler-bank":
                        return i18n.str`payto://x-taler-bank/[bank-host]/[receiver-account]?message=[subject]&amount=[${limitWithFee.currency}:X.Y]`;
                      case "iban":
                        return i18n.str`payto://iban/[receiver-iban]?message=[subject]&amount=[${limitWithFee.currency}:X.Y]`;
                    }
                  })()}
                  onInput={(e) => rawPaytoInputSetter(e.currentTarget.value)}
                />
                <ShowInputErrorLabel
                  message={errorsPayto?.rawPaytoInput}
                  isDirty={rawPaytoInput !== undefined}
                />
              </div>
            </div>
          )}

          {parsedSendingAmount && totalDebit ? (
            <dl class="mt-6 divide-y divide-onBackground/10 rounded-lg bg-background px-4 py-2 text-sm ring-1 ring-onBackground/10">
              <div class="flex items-center justify-between py-2">
                <dt class="text-gray-600">
                  <i18n.Translate>Recipient receives</i18n.Translate>
                </dt>
                <dd class="font-medium text-onBackground">
                  <RenderAmount
                    value={parsedSendingAmount}
                    spec={config.currency_specification}
                  />
                </dd>
              </div>
              <div class="flex items-center justify-between py-2">
                <dt class="text-gray-600">
                  <i18n.Translate>Bank fee</i18n.Translate>
                </dt>
                <dd class="text-onBackground">
                  <RenderAmount
                    value={wireFee}
                    spec={config.currency_specification}
                  />
                </dd>
              </div>
              <div class="flex items-center justify-between py-2">
                <dt class="font-medium text-onBackground">
                  <i18n.Translate>Total debited</i18n.Translate>
                </dt>
                <dd class="font-semibold text-onBackground">
                  <RenderAmount
                    value={totalDebit}
                    spec={config.currency_specification}
                  />
                </dd>
              </div>
            </dl>
          ) : undefined}

          {routeCashout && config.allow_conversion ? (
            <p class="mt-6 text-sm text-gray-600">
              <i18n.Translate>
                Need to move money out of this bank?
              </i18n.Translate>{" "}
              <a
                name="do cashout"
                href={routeCashout.url({})}
                class="font-semibold text-brand hover:underline"
              >
                <i18n.Translate>Start a cashout</i18n.Translate>
              </a>
            </p>
          ) : undefined}
        </div>
      </div>

      <div class="border-t border-onBackground/10 bg-background/60 px-5 py-4 sm:px-8">
        <OperationError class="mb-4" />
        <div class="flex items-center justify-between gap-x-6">
          {routeCancel ? (
            <a
              name="cancel"
              href={routeCancel.url({})}
              class="text-sm font-semibold leading-6 text-onBackground hover:underline"
            >
              <i18n.Translate>Cancel</i18n.Translate>
            </a>
          ) : (
            <div />
          )}
          <AsyncButton
            submit
            name="send"
            class="cursor-pointer rounded-md bg-primary px-4 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-default disabled:opacity-50"
            disabled={sendDisabled}
            onClick={() => send.run(creds!, sAmount!, parsedURI!)}
          >
            <i18n.Translate>Send transfer</i18n.Translate>
          </AsyncButton>
        </div>
      </div>
    </form>
  );
}

/**
 * Show the element when the load ended
 * @param element
 */
export function doAutoFocus(element: HTMLElement | null) {
  if (element) {
    setTimeout(() => {
      element.focus({ preventScroll: true });
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }, 100);
  }
}

export function InputAmount(
  {
    currency,
    name,
    value,
    left,
    placeholder,
    ariaLabelledby,
    onChange,
  }: {
    currency: string;
    name: string;
    left?: boolean | undefined;
    value: string | undefined;
    placeholder?: string | undefined;
    ariaLabelledby?: string | undefined;
    onChange?: (s: string) => void;
  },
  ref: Ref<HTMLInputElement>,
): VNode {
  const { config } = useBankCoreApiContext();
  return (
    <div class="mt-2">
      <div class="flex rounded-md shadow-sm border-0 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary">
        <div class="pointer-events-none inset-y-0 flex items-center px-3">
          <span class="text-gray-500 sm:text-sm">{currency}</span>
        </div>
        <input
          type="number"
          data-left={left}
          class="disabled:bg-gray-200 text-right rounded-md rounded-l-none data-[left=true]:text-left w-full py-1.5 pl-3 text-onBackground  placeholder:text-gray-400  sm:text-sm sm:leading-6"
          placeholder={placeholder ?? "0.00"}
          aria-describedby="price-currency"
          aria-labelledby={ariaLabelledby}
          ref={ref}
          name={name}
          id={name}
          autocomplete="off"
          value={value ?? ""}
          disabled={!onChange}
          onInput={(e) => {
            if (!onChange) return;
            const l = e.currentTarget.value.length;
            const sep_pos = e.currentTarget.value.indexOf(FRAC_SEPARATOR);
            if (
              sep_pos !== -1 &&
              l - sep_pos - 1 >
                config.currency_specification.num_fractional_input_digits
            ) {
              e.currentTarget.value = e.currentTarget.value.substring(
                0,
                sep_pos +
                  config.currency_specification.num_fractional_input_digits +
                  1,
              );
            }
            onChange(e.currentTarget.value);
          }}
        />
      </div>
    </div>
  );
}

function validateRawPayto(
  parsed: Paytos.URI,
  limit: AmountJson,
  host: string,
  i18n: InternationalizationAPI,
  type: "iban" | "x-taler-bank",
): TranslatedString | undefined {
  let result: TranslatedString | undefined;
  switch (type) {
    case "x-taler-bank": {
      if (parsed.targetType !== "x-taler-bank") {
        return i18n.str`Only "x-taler-bank" targets are supported`;
      }

      if (parsed.host !== host) {
        return i18n.str`Only this host is allowed. Use "${host}"`;
      }

      if (!parsed.account) {
        return i18n.str`Account name is missing`;
      }
      const result = validateTalerBank(parsed.account, i18n);
      if (result) return result;
      break;
    }
    case "iban": {
      if (parsed.targetType !== "iban") {
        return i18n.str`Only "IBAN" targets are supported`;
      }
      const result = validateIBAN(parsed.iban, i18n);
      if (result) return result;
      break;
    }
    default:
      assertUnreachable(type);
  }
  if (!parsed.params.amount) {
    return i18n.str`Missing "amount" parameter to specify the amount to be transferred`;
  }
  const amount = Amounts.parse(parsed.params.amount);
  if (!amount) {
    return i18n.str`The "amount" parameter is not valid`;
  }
  result = validateAmount(amount, limit, i18n);
  if (result) return result;

  if (!parsed.params.message) {
    return i18n.str`"message" parameters to specify a reference text for the transfer are missing`;
  }
  result = validateSubject(parsed.params.message, i18n);
  if (result) return result;

  return undefined;
}

function validateAmount(
  amount: AmountJson,
  limit: AmountJson,
  i18n: InternationalizationAPI,
): TranslatedString | undefined {
  if (amount.currency !== limit.currency) {
    return i18n.str`The only currency allowed is "${limit.currency}"`;
  }
  if (Amounts.isZero(amount)) {
    return i18n.str`You cannot transfer an amount of zero.`;
  }
  if (Amounts.cmp(limit, amount) === -1) {
    return i18n.str`The balance is not sufficient`;
  }
  return undefined;
}

function validateSubject(
  text: string,
  i18n: InternationalizationAPI,
): TranslatedString | undefined {
  if (text.length < 2) {
    return i18n.str`Please enter a longer subject`;
  }
  return undefined;
}

interface PaytoFieldProps {
  id: string;
  label: TranslatedString;
  required?: boolean;
  help?: TranslatedString;
  placeholder?: TranslatedString;
  error: string | undefined;
  value: string | undefined;
  rightIcons?: VNode;
  onChange: (p: string) => void;
  focus?: boolean;
  disabled?: boolean;
}

function Wrapper({
  withIcon,
  children,
}: {
  withIcon: boolean;
  children: ComponentChildren;
}): VNode {
  if (withIcon) {
    return <div class="flex justify-between">{children}</div>;
  }
  return <Fragment>{children}</Fragment>;
}

export function TextField({
  id,
  label,
  help,
  focus,
  disabled,
  onChange,
  placeholder,
  rightIcons,
  required,
  value,
  error,
}: PaytoFieldProps): VNode {
  return (
    <div class="sm:col-span-5">
      <label
        for={id}
        class="block text-sm font-medium leading-6 text-onBackground"
      >
        {label}
        {required && <b class="text-[red]"> *</b>}
      </label>
      <div class="mt-2">
        <Wrapper withIcon={rightIcons !== undefined}>
          <input
            ref={focus ? doAutoFocus : undefined}
            type="text"
            class="block w-full disabled:bg-gray-200 rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
            name={id}
            id={id}
            disabled={disabled}
            value={value ?? ""}
            placeholder={placeholder}
            autocomplete="off"
            required
            onInput={(e): void => {
              onChange(e.currentTarget.value);
            }}
          />
          {rightIcons}
        </Wrapper>
        <ShowInputErrorLabel message={error} isDirty={value !== undefined} />
      </div>
      {help && <p class="mt-2 text-sm text-gray-500">{help}</p>}
    </div>
  );
}

const PAYTO_START_REGEX = /(?:payto:\/\/[^\s]*)/;

function searchPaytoInHumanReadableText(
  text: string | undefined,
): string | undefined {
  if (!text) return undefined;
  const result = PAYTO_START_REGEX.exec(text);
  if (!result) return undefined;
  return result[0];
}
